import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  Article,
  Assessment,
  CheckCircle,
  RadioButtonUnchecked,
  Folder,
  FolderOpen
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface Chapter {
  template_chapter_id: number;
  title: string;
  actual_title?: string;
  is_indicator_driven?: boolean;
  content_status?: string;
  children?: Chapter[];
}

interface ChapterTreeProps {
  chapters: Chapter[];
  selectedChapter: Chapter | null;
  onChapterSelect: (chapter: Chapter) => void;
}

const ChapterTreeItem = ({ 
  chapter, 
  selectedChapter, 
  onChapterSelect, 
  level = 0 
}: {
  chapter: Chapter;
  selectedChapter: Chapter | null;
  onChapterSelect: (chapter: Chapter) => void;
  level?: number;
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  
  const hasChildren = chapter.children && chapter.children.length > 0;
  const isSelected = selectedChapter?.template_chapter_id === chapter.template_chapter_id;
  
  // 获取章节状态图标
  const getStatusIcon = () => {
    if (chapter.is_indicator_driven) {
      return <Assessment color="primary" fontSize="small" />;
    }
    
    if (chapter.content_status === 'final') {
      return <CheckCircle color="success" fontSize="small" />;
    }
    
    return <RadioButtonUnchecked color="disabled" fontSize="small" />;
  };

  // 获取Tooltip内容
  const getTooltipContent = () => {
    const parts = [];
    
    if (chapter.is_indicator_driven) {
      parts.push('类型: 指标驱动章节');
    } else {
      parts.push('类型: QA驱动章节');
    }
    
    if (chapter.content_status === 'final') {
      parts.push('状态: 已定稿');
    } else if (chapter.content_status === 'draft') {
      parts.push('状态: 草稿');
    } else if (chapter.content_status === 'user_edited') {
      parts.push('状态: 待审核');
    } else {
      parts.push('状态: 待编辑');
    }
    
    return parts.join('\n');
  };

  const handleClick = () => {
    if (hasChildren) {
      setExpanded(!expanded);
    } else {
      onChapterSelect(chapter);
    }
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  return (
    <>
      <ListItem 
        disablePadding 
        sx={{ 
          pl: level * 2,
          bgcolor: isSelected ? 'action.selected' : 'transparent',
          '&:hover': {
            bgcolor: 'action.hover'
          }
        }}
      >
        <Tooltip 
          title={hasChildren ? '' : getTooltipContent()} 
          placement="right"
          arrow
        >
          <ListItemButton 
            onClick={handleClick}
            sx={{ 
              py: 0.5,
              minHeight: 40,
              width: '100%',
              '&.Mui-selected': {
                bgcolor: 'primary.light',
                '&:hover': {
                  bgcolor: 'primary.light'
                }
              }
            }}
            selected={isSelected}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {hasChildren ? (
                expanded ? <FolderOpen fontSize="small" /> : <Folder fontSize="small" />
              ) : (
                getStatusIcon()
              )}
            </ListItemIcon>
            
            <ListItemText
              primary={
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: level === 0 ? '0.9rem' : '0.85rem',
                    color: hasChildren ? 'text.primary' : 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {chapter.actual_title || chapter.title}
                </Typography>
              }
            />
            
            {hasChildren && (
              <IconButton 
                size="small" 
                onClick={handleExpandClick}
                sx={{ p: 0.5 }}
              >
                {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            )}
          </ListItemButton>
        </Tooltip>
      </ListItem>

      {/* 子章节 */}
      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {chapter.children!.map(child => (
              <ChapterTreeItem
                key={child.template_chapter_id}
                chapter={child}
                selectedChapter={selectedChapter}
                onChapterSelect={onChapterSelect}
                level={level + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const ChapterTree: React.FC<ChapterTreeProps> = ({ chapters, selectedChapter, onChapterSelect }) => {
  const theme = useTheme();

  if (!chapters || chapters.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          暂无章节
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      flexGrow: 1, 
      overflow: 'auto',
      paddingBottom: '20px', // 添加底部内边距，确保最后一项不被遮挡
      // 强制显示滚动条样式
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        background: theme.palette.divider,
        borderRadius: '3px',
        '&:hover': {
          background: theme.palette.action.hover,
        },
      },
    }}>
      <List dense sx={{ paddingBottom: '20px' }}>
        {chapters.map((chapter) => (
          <ChapterTreeItem
            key={chapter.template_chapter_id}
            chapter={chapter}
            selectedChapter={selectedChapter}
            onChapterSelect={onChapterSelect}
          />
        ))}
      </List>
    </Box>
  );
};

export default ChapterTree;